import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Collapse,
    IconButton,
    Paper,
    Tooltip,
    Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import MapIcon from '@mui/icons-material/Map';

import { IncidentSummary, useProjectIncidents } from '../../api/incidentLog';
import { AgentSessionSummary, useProjectAgentSessions } from '../../api/agentSessions';
import IncidentStatusChip from '../incidents/IncidentStatusChip';
import IncidentLinkChips from '../incidents/IncidentLinkChips';
import CloseIncidentDialog from '../incidents/CloseIncidentDialog';
import { AgentStatusChip, MonitorButton, kindIcon } from '../incidents/incidentBits';
import {
    INCIDENT_STATUS_GROUPS,
    extraKinds,
    formatWhen,
    incidentTitle,
} from '../../utils/incidentRekindle';
import { realtimeKindLabel } from '../../utils/realtimeKindLabels';

function IncidentCard({
    projectId,
    incident,
    agentSession,
    agentEnabled,
    onOpen,
    onOpenDetail,
    onSelectIncident,
    onRequestClose,
}: {
    projectId: string;
    incident: IncidentSummary;
    agentSession: AgentSessionSummary | undefined;
    agentEnabled: boolean;
    onOpen: () => void;
    onOpenDetail: () => void;
    onSelectIncident?: (incidentId: number) => void;
    onRequestClose: () => void;
}) {
    const summary = agentSession?.latest_summary;
    return (
        <Paper
            variant="outlined"
            onClick={onOpen}
            sx={{
                p: 2,
                mb: 1.5,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                cursor: 'pointer',
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                borderColor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                    borderColor: '#00d4ad',
                    backgroundColor: 'rgba(0, 212, 173, 0.05)',
                },
            }}
        >
            {/* Opening kind's icon, plus the others this incident absorbed —
                hotspot, ignition and burnt-area detections at one place are
                one incident. */}
            <Tooltip
                title={(incident.kinds ?? [incident.kind]).map(realtimeKindLabel).join(' + ')}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0, mt: 0.25 }}>
                    {kindIcon(incident.kind)}
                    {extraKinds(incident).map((k) => (
                        <Box key={k} sx={{ display: 'flex', opacity: 0.75, transform: 'scale(0.8)' }}>
                            {kindIcon(k)}
                        </Box>
                    ))}
                </Box>
            </Tooltip>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
                    <Typography
                        component="span"
                        sx={{
                            color: 'rgba(255,255,255,0.55)',
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            flexShrink: 0,
                        }}
                    >
                        #{incident.id}
                    </Typography>
                    <Typography sx={{ color: '#fff', fontWeight: 600 }} noWrap>
                        {incidentTitle(incident)}
                    </Typography>
                    <IncidentStatusChip status={incident.status} />
                    {agentSession?.status === 'active' && <AgentStatusChip session={agentSession} />}
                </Box>
                <Typography sx={{ color: '#e2e8f0', fontSize: '0.82rem', mt: 0.5 }}>
                    {incident.headline}
                </Typography>
                {summary && agentSession?.status === 'active' && (
                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.7)',
                            fontSize: '0.78rem',
                            mt: 0.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}
                    >
                        Agent: {summary.body || summary.title}
                    </Typography>
                )}
                <Box sx={{ mt: 0.75 }}>
                    <IncidentLinkChips
                        incident={incident}
                        projectId={projectId}
                        onSelectIncident={onSelectIncident}
                    />
                </Box>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.55)', fontSize: '0.75rem', mt: 0.5 }}>
                    {incident.detections} detection{incident.detections === 1 ? '' : 's'} · first{' '}
                    {formatWhen(incident.first_detected_at)} · last {formatWhen(incident.last_detected_at)}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                {agentSession?.status !== 'active' && agentEnabled && incident.closed_at === null && (
                    <MonitorButton projectId={projectId} incident={incident} onStarted={onOpenDetail} />
                )}
                {incident.closed_at === null && (
                    <Tooltip title="Close incident — mark this event as over">
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                // The whole card selects on click; closing must not.
                                e.stopPropagation();
                                onRequestClose();
                            }}
                            sx={{ color: 'rgba(255,255,255,0.55)', '&:hover': { color: '#00d4ad' } }}
                            aria-label="Close incident"
                        >
                            <TaskAltIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip title="Full details">
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onOpenDetail();
                        }}
                        sx={{ color: 'rgba(255,255,255,0.55)', '&:hover': { color: '#00d4ad' } }}
                        aria-label="Incident details"
                    >
                        <ChevronRightIcon />
                    </IconButton>
                </Tooltip>
            </Box>
        </Paper>
    );
}

interface IncidentsTabProps {
    /** Passed explicitly — useParams breaks when opened from the listing page. */
    projectId?: string;
    /** Project map: a card click shows the incident on the map behind
     *  (the listing page, with no map, navigates to it instead). */
    onSelectIncident?: (incident: IncidentSummary) => void;
}

export default function IncidentsTab({ projectId, onSelectIncident }: IncidentsTabProps) {
    const navigate = useNavigate();
    const { data } = useProjectIncidents(projectId, { days: 0 });
    const { data: agentData } = useProjectAgentSessions(projectId);
    const [closing, setClosing] = useState<IncidentSummary | null>(null);
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
        attention: true,
        watching: true,
        closed: false,
    });
    const incidents = data?.data?.incidents ?? [];
    const agentSessions = agentData?.data?.sessions ?? {};
    const agentEnabled = agentData?.data?.enabled ?? false;
    const byId = new Map(incidents.map((i) => [i.id, i]));

    const openOnMap = (incident: IncidentSummary) => {
        if (onSelectIncident) onSelectIncident(incident);
        else navigate(`/projects/${projectId}/incidents/${incident.id}`);
    };
    const openDetail = (incident: IncidentSummary) =>
        navigate(`/projects/${projectId}/incidents/${incident.id}`);

    return (
        <Box sx={{ maxWidth: 720, mx: 'auto', p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 500, color: '#fff' }}>
                Incidents
            </Typography>
            <Typography sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9rem' }}>
                Every detection event tracked near this project, grouped by what needs
                doing. Click a card to see it on the map
                {agentEnabled ? ', or have an AI agent monitor it' : ''}; the arrow
                opens its full record.
            </Typography>

            {incidents.length === 0 ? (
                <Paper
                    variant="outlined"
                    sx={{
                        p: 4,
                        textAlign: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                    }}
                >
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.9rem' }}>
                        No incidents yet. Events appear here once realtime detections
                        are enabled and someone subscribes to notifications.
                    </Typography>
                </Paper>
            ) : (
                INCIDENT_STATUS_GROUPS.map((group) => {
                    const members = incidents.filter((i) => group.statuses.includes(i.status));
                    if (!members.length) return null;
                    const open = openGroups[group.key] ?? true;
                    return (
                        <Box key={group.key} sx={{ mb: 2 }}>
                            <Box
                                onClick={() => setOpenGroups((g) => ({ ...g, [group.key]: !open }))}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    cursor: 'pointer',
                                    mb: 1,
                                    color: 'rgba(255,255,255,0.75)',
                                }}
                            >
                                {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                                <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    {group.label}
                                </Typography>
                                <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)' }}>
                                    ({members.length})
                                </Typography>
                                {onSelectIncident && (
                                    <MapIcon sx={{ fontSize: 16, ml: 'auto', color: 'rgba(255,255,255,0.35)' }} />
                                )}
                            </Box>
                            <Collapse in={open}>
                                {members.map((incident) => (
                                    <IncidentCard
                                        key={incident.id}
                                        projectId={projectId ?? ''}
                                        incident={incident}
                                        agentSession={agentSessions[String(incident.id)]}
                                        agentEnabled={agentEnabled}
                                        onOpen={() => openOnMap(incident)}
                                        onOpenDetail={() => openDetail(incident)}
                                        onSelectIncident={(id) => {
                                            const target = byId.get(id);
                                            if (target) openOnMap(target);
                                            else navigate(`/projects/${projectId}/incidents/${id}`);
                                        }}
                                        onRequestClose={() => setClosing(incident)}
                                    />
                                ))}
                            </Collapse>
                        </Box>
                    );
                })
            )}

            <CloseIncidentDialog
                projectId={projectId}
                incident={closing}
                hasAgent={closing !== null && agentSessions[String(closing.id)]?.status === 'active'}
                onClose={() => setClosing(null)}
            />
        </Box>
    );
}
